import React from 'react';
import { Chip, Stack, Tooltip } from '@mui/material';
import { CheckCircle, Schedule, ErrorOutline } from '@mui/icons-material';

/**
 * Badges de estado de una solicitud para el backoffice.
 *
 * Los tres ejes son independientes y no se pueden derivar de `status_dwolla_id`:
 *
 *  - Proveedor elegido (`provider_choice`): usio / dwolla / ambos / ninguno.
 *  - Origen (`origin`): si la solicitud nace del onboarding o del modulo
 *    `/payment-setup` del subdominio. Es lo que pidio negocio para poder
 *    distinguir las altas tardias.
 *  - Estado de cobro (`payment_ready`): si el proveedor ya lo aprobo. Es el unico
 *    indicador de "puede cobrar", y NO es un `status_*_id`.
 */

interface Props {
  /** Fila cruda del listado (`res.data[i]`), no el `info_dwolla` aplanado. */
  request?: any;
  size?: 'small' | 'medium';
}

const PROVIDER_LABELS: Record<string, string> = {
  usio: 'Usio',
  dwolla: 'Dwolla',
  both: 'Usio + Dwolla',
};

const PROVIDER_COLORS: Record<string, 'primary' | 'secondary' | 'default'> = {
  usio: 'primary',
  dwolla: 'secondary',
  both: 'default',
};

const RequestBadges: React.FC<Props> = ({ request, size = 'small' }) => {
  if (!request) return null;

  const provider = request.provider_choice as string | null;
  const isDeferred = request.deferred === true || !provider;
  const paymentReady = request.payment_ready === true;
  const fromSubdomain = request.origin === 'subdomain';

  return (
    <Stack
      direction='row'
      spacing={0.5}
      flexWrap='wrap'
      useFlexGap
    >
      <Chip
        size={size}
        label={isDeferred ? 'No provider yet' : PROVIDER_LABELS[provider] || provider}
        color={isDeferred ? 'warning' : PROVIDER_COLORS[provider] || 'default'}
        variant={isDeferred ? 'outlined' : 'filled'}
      />

      <Tooltip title={fromSubdomain ? 'Created from the lawyer portal' : 'Created during onboarding'}>
        <Chip
          size={size}
          label={fromSubdomain ? 'From subdomain' : 'Onboarding'}
          variant='outlined'
          color={fromSubdomain ? 'info' : 'default'}
        />
      </Tooltip>

      {paymentReady ? (
        <Chip
          size={size}
          icon={<CheckCircle />}
          label='Ready to collect'
          color='success'
          variant='outlined'
        />
      ) : (
        <Chip
          size={size}
          icon={isDeferred ? <Schedule /> : <ErrorOutline />}
          label={isDeferred ? 'Pending setup' : 'In review'}
          color={isDeferred ? 'warning' : 'default'}
          variant='outlined'
        />
      )}
    </Stack>
  );
};

export default RequestBadges;
